import {
  Creative,
  FrameRange,
  VideoCreativeLayer,
} from 'content-creation-common/models';
import { media as mediaConstants } from 'content-creation-common/constants';

import { FrameProviderPrioritizationInterface } from './video/FrameProviderPrioritizationInterface';
import { GlobalContentWatcher } from '../content/watcher/ContentWatcher';
import HTML5FrameProvider from './video/HTML5FrameProvider';
import Mp4FrameProvider from './video/Mp4FrameProvider';
import ServerFrameProvider from './video/ServerFrameProvider';

import VideoFrameCache from './video/VideoFrameCache';
import Watchable from '../../model/watcher/Watchable';
import WatchedContentData from '../content/watcher/WatchedContentData';

import { SHOT_ELEMENT_TYPES } from '../video-reformatting/constants';

import { isEmptyString, isNil } from '../../util/object';
import { isNotNumber } from '../../util/numbers';
import { isEmpty as isEmptyArray, sortBy } from '../../util/array';
import { getVideoFile, shouldUsePngSequence } from './utils';
import { info } from '../../util/log';
import Resolution from './video/Resolution';
import FrameProvider from './video/FrameProvider';
import {
  frameManagementPerformanceReview,
  MEASUREMENT_KEYS,
} from '../debug/measure/PerformanceReview';

const { MEDIA_PROCESSORS } = mediaConstants;

const watchable = new Watchable();
GlobalContentWatcher.watch(watchable);

/**
 * Singleton for managing frame extractions for content. Frames are fetched
 * asynchronously and stored in a cache for later retrieval.
 *
 * Note: This frame manager currently only supports MP4 videos that have been transcoded
 * for the Broadway decoder. In the future, this will be a starting point for all
 * methods of frame extraction.
 */
class FrameManager {
  /** @property {Map<string, VideoFrameCache>} */
  _videoFrameCaches = new Map();

  /** @property {Map<string, FrameProvider>} */
  _frameProviderByCacheId = new Map();

  /** @property {Map<string, function>} */
  _frameReadyHandlers = new Map();

  /** @property {Map<string, function>} */
  _contentFrameHandlers = new Map();

  /** @property {Number} */
  _currentFrame = null;

  /** @property {Mp4FrameProvider} */
  _mp4FrameProvider;

  /** @property {ServerFrameProvider} */
  _serverFrameProvider;

  /** @property {HTML5FrameProvider} */
  _HTML5FrameProvider;

  /** @property {Set} */
  _hasReceivedFirstFrame = new Map();

  constructor() {
    info('Initializing Frame Manager');
    this._mp4FrameProvider = new Mp4FrameProvider();
    this._mp4FrameProvider.addFrameReadyListener(this._onRasterImage);
    this._serverFrameProvider = new ServerFrameProvider();
    this._serverFrameProvider.addFrameReadyListener(this._onRasterImage);
    this._HTML5FrameProvider = new HTML5FrameProvider(
      Resolution.getMediumResolution(),
    );
    this._HTML5FrameProvider.addFrameReadyListener(this._onRasterImage);
  }

  /**
   * Find all video content for frame extraction.
   * This method assumes all valid content can be extracted using one of the downstream extraction methods.
   * @param {Creative} creative
   * @param {FrameRange[]} requestedFrameRanges a list of frame ranges to request for extraction
   * @param {int} frameIndexToPrioritize frame number that will be used to prioritize extraction
   * @returns {{bitmap: ImageBitmap, isCacheHit: boolean}}
   */
  extractFramesFromCreative = (
    creative,
    requestedFrameRanges,
    frameIndexToPrioritize = 0,
  ) => {
    if (!Creative.isCreative(creative)) {
      throw new Error('Must provide Creative to extractFramesFromCreative');
    }

    const videoCreativeLayers = Creative.getCreativeLayersByType(
      creative,
      VideoCreativeLayer,
    );

    const sortedLayers = sortBy([...videoCreativeLayers], 'frameStart');

    // set up or update new video
    const allMediaIdentifiers = new Set();
    const allVideoCreativeLayerIds = new Set();
    sortedLayers.forEach((videoCreativeLayer) => {
      const mediaIdentifier = this._setUpVideoFrameCaches(
        videoCreativeLayer,
        requestedFrameRanges,
      );

      if (!isEmptyString(mediaIdentifier)) {
        allMediaIdentifiers.add(mediaIdentifier);
        allVideoCreativeLayerIds.add(videoCreativeLayer.elementId);
      }
    });

    // extract frames
    this._extractFramesFromCache(requestedFrameRanges, frameIndexToPrioritize);

    // delete unused caches and video layers
    this._videoFrameCaches.forEach((videoFrameCache, key, map) => {
      // delete the entire cache
      if (!allMediaIdentifiers.has(key)) {
        const { id: cacheId } = videoFrameCache;
        const frameProvider = this._frameProviderByCacheId.get(cacheId);
        if (!FrameProvider.isFrameProvider(frameProvider)) return;

        frameProvider.cancel(cacheId);
        map.delete(key);
        return;
      }

      // delete unused video creative layers
      videoFrameCache.videoCreativeLayers.forEach(
        (videoCreativeLayer, elementId, layerMap) => {
          if (allVideoCreativeLayerIds.has(elementId)) return;
          layerMap.delete(elementId);
        },
      );
    });
  };

  extractFramesForCurrentFrame = (
    currentFrame,
    requestedFrameRanges,
    forcedFrameRange,
  ) => {
    if (this._currentFrame !== currentFrame) {
      this._currentFrame = currentFrame;
      this.prioritizeFrameForPlayback(
        currentFrame,
        requestedFrameRanges,
        forcedFrameRange,
      );
    }
  };

  /**
   * Determines if the video file caches have all of the image bitmaps
   * for the frames included in the array of FrameRanges.
   *
   * @param {array<FrameRange>} frameRanges
   * @returns {Boolean}
   */
  hasFramesForFrameRanges = (frameRanges) => {
    if (this._videoFrameCaches.size === 0) return false;
    for (const videoFrameCache of this._videoFrameCaches.values()) {
      const relativeFrameRanges = videoFrameCache.getRelativeFrameRanges(
        frameRanges,
      );

      if (!videoFrameCache.hasBitmapDataForFrameRanges(relativeFrameRanges)) {
        return false;
      }
    }

    return true;
  };

  /**
   * @function getFrameBitmapData Returns the bitmap data for a specified frame,
   * if it's not available, it will return the closest frame.
   * @param {CreativeLayer} creativeLayer
   * @param {int} frameIndex
   * @returns {{bitmap: ImageBitmap, isCacheHit: boolean}}
   */
  getFrameBitmapData = (creativeLayer, frameIndex) => {
    if (!VideoCreativeLayer.isVideoCreativeLayer(creativeLayer)) {
      throw new Error('Must provide a valid VideoCreativeLayer');
    }

    if (isNotNumber(frameIndex)) {
      throw new Error('Must provide a valid frameIndex');
    }

    const { media } = creativeLayer;

    if (isNil(media) || !media.loaded) return null;

    const videoFrameCache = this._videoFrameCaches.get(
      this._generateCacheIdFromVideoCreativeLayer(creativeLayer),
    );
    if (isNil(videoFrameCache)) return null;

    const hasBitmapData = videoFrameCache.hasFrameBitmapData(frameIndex);
    const bitmapData = videoFrameCache.getFrameBitmapData(frameIndex, true);
    if (isNil(bitmapData)) return null;
    return {
      bitmap: bitmapData,
      isCacheHit: hasBitmapData,
    };
  };

  /**
   * Locks the frame index in a given video creative layer
   * @param {VideoCreativeLayer} videoCreativeLayer
   * @param {int} frameIndex of frame to protect
   */
  lockFrameIndex = (videoCreativeLayer, frameIndex) => {
    const videoFrameCache = this.getCache(videoCreativeLayer);
    if (!VideoFrameCache.isVideoFrameCache(videoFrameCache)) return;

    const relativeFrameIndex = videoFrameCache.getRelativeFrameIndex(
      frameIndex,
    );
    if (relativeFrameIndex === -1) return;

    videoFrameCache.lockFrameIndex(relativeFrameIndex);
    // if frame currently does not exist, try loading it from the server directly
    if (!videoFrameCache.hasFrameBitmapData(relativeFrameIndex)) {
      this._serverFrameProvider.fetchFrames(
        new FrameProviderPrioritizationInterface(videoFrameCache.videoFile, [
          new FrameRange(relativeFrameIndex, relativeFrameIndex),
        ]),
      );
    }
  };

  /**
   * Unlocks the frame index for a given cacheId
   * @param {VideoCreativeLayer} videoCreativeLayer
   * @param {int} frameIndex of frame to unprotect
   */
  unlockFrameIndex = (videoCreativeLayer, frameIndex) => {
    const videoFrameCache = this.getCache(videoCreativeLayer);
    if (!VideoFrameCache.isVideoFrameCache(videoFrameCache)) return;

    this._videoFrameCaches.forEach((videoFrameCache) => {
      videoFrameCache.unlockFrameIndex(frameIndex);
    });
  };

  /**
   * Sets the priority frame index on each of the video file caches.
   * @param {int} frameIndex of the frame to prioritize
   * @param {FrameRange[]} requestedFrameRanges a list of frame ranges that are eligible for buffer
   * @param {FrameRange} forcedFrameRange frame range to extract regardless if layer is skipped or not visible
   */
  prioritizeFrameForPlayback = (
    frameIndex,
    requestedFrameRanges,
    forcedFrameRange = null,
  ) => {
    this._videoFrameCaches.forEach((videoFrameCache) => {
      const frameProvider = this._frameProviderByCacheId.get(
        videoFrameCache.id,
      );
      if (frameProvider !== this._mp4FrameProvider) return;

      this._prioritizeFrameIndexForCache(
        frameIndex,
        requestedFrameRanges,
        videoFrameCache,
        forcedFrameRange,
      );

      const unavailableFrameRanges = this._getUnavailableFrameRanges(
        videoFrameCache,
        requestedFrameRanges,
        forcedFrameRange,
        frameIndex,
      );

      this._requestNeededFrames(videoFrameCache, unavailableFrameRanges);
    });
  };

  cleanup = () => {
    info('Cleaning up FrameManager');
    const unavailableFrameRanges = [];
    this._videoFrameCaches.forEach((videoFrameCache) => {
      // Reset priorities for frame providers
      this._mp4FrameProvider.fetchFrames(
        new FrameProviderPrioritizationInterface(
          videoFrameCache.videoFile,
          unavailableFrameRanges,
        ),
      );
      videoFrameCache.cleanup();
    });
    this._videoFrameCaches.clear();
    this._frameReadyHandlers.clear();
    this._contentFrameHandlers.clear();
  };

  /**
   * @function _onRasterImage
   * @param  {RasteredImageBitmapInterface|CanvasImageBitmapInterface} rasteredImageBitmapInterface
   */
  _onRasterImage = (rasteredImageBitmapInterface) => {
    if (isNil(rasteredImageBitmapInterface)) return;
    this._cacheImageBitmap(
      rasteredImageBitmapInterface.mediaIdentifier,
      rasteredImageBitmapInterface.frameNumber,
      rasteredImageBitmapInterface.imageBitmap,
    );
    this._notifyFrameReadyListeners(
      rasteredImageBitmapInterface.mediaIdentifier,
      rasteredImageBitmapInterface.frameNumber,
    );
  };

  /**
   * @function _cacheImageBitmap
   * @param  {string} mediaIdentifier
   * @param  {number} frameNumber
   * @param  {ImageBitmap|HTMLCanvasElement} bitmap
   */
  _cacheImageBitmap(mediaIdentifier, frameNumber, bitmap) {
    if (
      frameNumber === 0 &&
      !this._hasReceivedFirstFrame.get(mediaIdentifier)
    ) {
      // Got first frame
      this._hasReceivedFirstFrame.set(mediaIdentifier, true);
      frameManagementPerformanceReview.end(
        MEASUREMENT_KEYS.TIME_TO_FIRST_FRAME,
        mediaIdentifier,
      );
      frameManagementPerformanceReview.log();
    }
    const videoFrameCache = this._videoFrameCaches.get(mediaIdentifier);
    if (isNil(videoFrameCache)) return;
    videoFrameCache.putFrameBitmapData(frameNumber, bitmap);
  }

  /**
   * @function _notifyFrameReadyListeners
   * @param  {String} mediaIdentifier
   * @param  {Number} contentFrameNumber
   */
  _notifyFrameReadyListeners(mediaIdentifier, contentFrameNumber) {
    const frameReadyHandlersForMedia = this._frameReadyHandlers.get(
      mediaIdentifier,
    );
    if (isNil(frameReadyHandlersForMedia)) return;
    frameReadyHandlersForMedia.forEach((readyHandler) => {
      if (isNil(readyHandler)) return;
      readyHandler(contentFrameNumber);
    });
  }

  _setUpVideoFrameCaches(videoCreativeLayer) {
    if (!VideoCreativeLayer.isVideoCreativeLayer(videoCreativeLayer)) {
      throw new Error('Must provide videoCreativeLayer');
    }

    const { contentId, element } = videoCreativeLayer;
    // TODO(omegdadi): Continue on only when a chunk variant is available. Look for that instead.
    if (element.meta.shotType === SHOT_ELEMENT_TYPES.SOURCE) return null;

    // Extractions are keyed by server id or media URL
    const mediaIdentifier = this._generateCacheIdFromVideoCreativeLayer(
      videoCreativeLayer,
    );

    const videoFile = getVideoFile(mediaIdentifier, videoCreativeLayer);
    if (isNil(videoFile)) return null;

    let videoFrameCache = this._videoFrameCaches.get(mediaIdentifier);

    const frameProvider = this._getFrameProvider(videoCreativeLayer);
    const hasProviderChanged =
      this._frameProviderByCacheId.get(mediaIdentifier) !== frameProvider;

    // set up all the video caches
    if (isNil(videoFrameCache)) {
      videoFrameCache = new VideoFrameCache(videoFile, videoCreativeLayer);
      videoFrameCache.numberOfBufferedFrames =
        frameProvider.numberOfBufferedFrames;
      videoFrameCache.disableChunkEviction = !videoCreativeLayer.processors.has(
        MEDIA_PROCESSORS.VIS,
      );

      info(`Creating video frame cache for ${mediaIdentifier}`);
      this._videoFrameCaches.set(mediaIdentifier, videoFrameCache);
      this._frameProviderByCacheId.set(
        mediaIdentifier,
        this._getFrameProvider(videoCreativeLayer),
      );
    } else {
      videoFrameCache.updateCacheIfNeeded(videoFile, videoCreativeLayer);
    }

    // if frame provider has changed, clear out cache
    if (hasProviderChanged) {
      this._frameProviderByCacheId.set(mediaIdentifier, frameProvider);
      videoFrameCache.clear();
    }

    // TODO: Move Content Watcher to Central Dispatch?
    if (!this._contentFrameHandlers.has(contentId)) {
      const frameHandler = this._getFrameReadyHandler(videoCreativeLayer);
      this._contentFrameHandlers.set(contentId, frameHandler);

      const frameReadyHandlersForMedia = this._frameReadyHandlers.get(
        mediaIdentifier,
      );
      if (isNil(frameReadyHandlersForMedia)) {
        this._frameReadyHandlers.set(mediaIdentifier, [frameHandler]);
      } else {
        frameReadyHandlersForMedia.push(frameHandler);
      }
    }

    return mediaIdentifier;
  }

  _extractFramesFromCache(requestedFrameRanges, frameIndex) {
    this._videoFrameCaches.forEach((videoFrameCache, mediaIdentifier) => {
      info(`Extracting frames from video frame cache for ${mediaIdentifier}`);

      if (!this._canExtractFrames(mediaIdentifier)) return;

      this._hasReceivedFirstFrame.set(mediaIdentifier, false);
      frameManagementPerformanceReview.start(
        MEASUREMENT_KEYS.TIME_TO_FIRST_FRAME,
        mediaIdentifier,
      );

      // need to use the right unavailableFrameRanges
      this._prioritizeFrameIndexForCache(
        frameIndex,
        requestedFrameRanges,
        videoFrameCache,
      );

      const unavailableFrameRanges = this._getUnavailableFrameRanges(
        videoFrameCache,
        requestedFrameRanges,
        null,
        frameIndex,
      );

      this._removeUnusedFramesFromCache(videoFrameCache, requestedFrameRanges);

      this._requestNeededFrames(videoFrameCache, unavailableFrameRanges);
    });
  }

  _prioritizeFrameIndexForCache = (
    frameIndex,
    requestedFrameRanges,
    videoFrameCache,
    forcedFrameRange,
  ) => {
    videoFrameCache.prioritizeFrameIndex(
      frameIndex,
      requestedFrameRanges,
      forcedFrameRange,
    );
  };

  /**
   * @function getCache
   * @param  {VideoCreativeLayer} videoCreativeLayer
   * @return {VideoFrameCache}
   */
  getCache = (videoCreativeLayer) => {
    const cacheIdentifier = this._generateCacheIdFromVideoCreativeLayer(
      videoCreativeLayer,
    );
    return this._videoFrameCaches.get(cacheIdentifier);
  };

  /**
   * @function hasCacheForMedia
   * @param  {object} media
   * @return {boolean}
   */
  hasCacheForMedia = (media) => {
    const cacheIdentifier = this._generateCacheIdFromMedia(media);
    return this._videoFrameCaches.has(cacheIdentifier);
  };

  _requestNeededFrames(videoFrameCache, requestedFrameRanges) {
    if (!VideoFrameCache.isVideoFrameCache(videoFrameCache)) return;
    if (isEmptyArray(requestedFrameRanges)) return;

    const { id, videoFile } = videoFrameCache;
    const frameProvider = this._frameProviderByCacheId.get(id);
    if (!FrameProvider.isFrameProvider(frameProvider)) {
      throw new Error('Frame Provider is invalid');
    }

    videoFrameCache.setRequestedFrames(requestedFrameRanges);
    if (isEmptyArray(requestedFrameRanges)) return;

    frameProvider.fetchFrames(
      new FrameProviderPrioritizationInterface(videoFile, requestedFrameRanges),
    );
  }

  _removeUnusedFramesFromCache(videoFrameCache, requestedFrameRanges) {
    if (!VideoFrameCache.isVideoFrameCache(videoFrameCache)) return;

    const frameProvider = this._frameProviderByCacheId.get(videoFrameCache.id);
    if (!FrameProvider.isFrameProvider(frameProvider)) {
      throw new Error('Frame Provider is undefined');
    }

    const relativeFrameRanges = videoFrameCache.getRelativeFrameRanges(
      requestedFrameRanges,
    );
    videoFrameCache.removeUnusedFrames(relativeFrameRanges);
  }

  /*
   * Unavailable Frames are calculated differently based on the frame extraction method:
   * a) HTML5 - requires relative frame ranges based on the video frames since requested frameRanges are based off of the creative
   * b) MP4 is based off of chunk index
   */
  _getUnavailableFrameRanges = (
    videoFrameCache,
    requestedFrameRanges,
    forcedFrameRange,
    frameIndex = 0,
  ) => {
    // get prioritized relative frame ranges
    // refactors to have frame provider truncate
    const prioritizedFrameRanges = videoFrameCache.getPrioritizedRelativeFrameRanges(
      requestedFrameRanges,
      forcedFrameRange,
      frameIndex,
      videoFrameCache.numberOfBufferedFrames,
    );

    // get unavailable frame ranges
    const checkRequestedFrames = this._shouldCheckRequestedFrames(
      videoFrameCache,
    );
    return videoFrameCache.getUnavailableFrameRanges(
      prioritizedFrameRanges,
      checkRequestedFrames,
    );
  };

  _getFrameReadyHandler = (videoCreativeLayer) => {
    return (contentFrameNumber) => {
      // Get the content-relative frame ranges
      const relativeFrameRanges = videoCreativeLayer.getRelativeFrameIndicesAndTimesAtFrame(
        0,
      );
      const { endIndex, startIndex } = relativeFrameRanges;

      if (startIndex <= contentFrameNumber && endIndex >= contentFrameNumber) {
        const data = new WatchedContentData(videoCreativeLayer.frameRange);
        watchable.notifyWatcher(data);
      }
    };
  };

  _canExtractFrames = (mediaIdentifier) => {
    const videoFrameCache = this._videoFrameCaches.get(mediaIdentifier);
    if (!VideoFrameCache.isVideoFrameCache(videoFrameCache)) return false;

    const { videoFile } = videoFrameCache;
    const frameProvider = this._frameProviderByCacheId.get(mediaIdentifier);
    return (
      frameProvider === this._HTML5FrameProvider ||
      !isEmptyString(videoFile.serverId)
    );
  };

  _getFrameProvider(videoCreativeLayer) {
    if (!VideoCreativeLayer.isVideoCreativeLayer(videoCreativeLayer)) {
      throw new Error('Must provide valid VideoCreativeLayer');
    }

    if (videoCreativeLayer.processors.has(MEDIA_PROCESSORS.VIS)) {
      return this._mp4FrameProvider;
    }

    if (shouldUsePngSequence(videoCreativeLayer.media)) {
      return this._serverFrameProvider;
    }

    return this._HTML5FrameProvider;
  }

  _generateCacheIdFromMedia(media) {
    if (isNil(media)) return null;
    return media.localUrl || media.serverId;
  }

  _generateCacheIdFromVideoCreativeLayer(videoCreativeLayer) {
    if (
      !VideoCreativeLayer.isVideoCreativeLayer(videoCreativeLayer) ||
      isNil(videoCreativeLayer.media)
    ) {
      throw new Error('Must provide a valid videoCreativeLayer');
    }

    return this._generateCacheIdFromMedia(videoCreativeLayer.media);
  }

  _shouldCheckRequestedFrames(videoFrameCache) {
    const { id: cacheId } = videoFrameCache;
    const frameProvider = this._frameProviderByCacheId.get(cacheId);

    return frameProvider !== this._mp4FrameProvider;
  }
}

const frameManager = new FrameManager();
export default frameManager;
